import type { UpsertUserTablePreferencesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpsertUserTablePreferencesInput {
  TableId: string;
  Preferences: unknown;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation response shape
 * so consumers branching on the result do not need to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): UpsertUserTablePreferencesMutation => ({
  insert_user_table_preferences: {
    __typename: 'user_table_preferences_mutation_response',
    affected_rows: data.affected_rows,
  },
});

export const useUpsertUserTablePreferencesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.userTablePreferences.upsertUserTablePreferences.mutationOptions(
      {
        onSuccess: async (_data, variables) => {
          // Mirror the legacy Apollo refetch of this table's preferences read,
          // which is keyed by tableId.
          await queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.userTablePreferences.userTablePreferences.queryKey({
                tableId: variables.tableId,
              }),
          });
        },
      }
    ),
  });

  return {
    upsertUserTablePreferences: async (
      input: UpsertUserTablePreferencesInput
    ): Promise<UpsertUserTablePreferencesMutation> => {
      const result = await mutation.mutateAsync({
        tableId: input.TableId,
        Preferences: input.Preferences,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
